import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useSite } from "@/lib/content";
import { SocialLinks } from "@/components/site/SocialIcons";

const LINKS = [
  { to: "/", label: "HOME" },
  { to: "/about", label: "ABOUT" },
  { to: "/dates", label: "DATES" },
  { to: "/music", label: "MUSIC" },
  { to: "/media", label: "MEDIA" },
  { to: "/booking", label: "BOOKING" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { data: site } = useSite();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const solid = scrolled || open;

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          solid ? "bg-coal/90 backdrop-blur-xl border-b border-line" : "bg-transparent"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex items-center justify-between px-5 md:px-10 h-16 md:h-20">
          <Link to="/" data-testid="nav-logo-link" aria-label="SEIMEN DEXTER — home" className="flex items-center">
            <img
              src="/assets/original_logo.png"
              alt="SEIMEN DEXTER"
              className="h-11 md:h-14 w-auto object-contain"
              draggable="false"
            />
          </Link>
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-xs tracking-[0.25em] transition-colors ${
                    isActive ? "text-white" : "text-white/60 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:block">
            <SocialLinks socials={site?.socials} className="gap-4" iconClass="h-4 w-4" />
          </div>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 -mr-2 text-white"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ink lg:hidden flex flex-col justify-between pt-28 pb-10 px-6"
          >
            <nav className="flex flex-col gap-3" aria-label="Mobile navigation">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `font-display text-6xl leading-none ${
                        isActive ? "text-bloodbright" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="flex flex-col gap-8"
            >
              <SocialLinks socials={site?.socials} className="gap-x-6 gap-y-4 flex-wrap" iconClass="h-5 w-5" showLabels />
              <p className="text-[11px] tracking-[0.25em] text-white/40">
                HARD / INDUSTRIAL TECHNO — 165 BPM
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
